import { FoodsIcon } from "@foodpilot/foods";
import Dashboard from "@mui/icons-material/Dashboard";
import Apartment from "@mui/icons-material/Apartment";
import Class from "@mui/icons-material/Class";
import EmojiTransportation from "@mui/icons-material/EmojiTransportation";
import LocalShipping from "@mui/icons-material/LocalShipping";
import Person from "@mui/icons-material/Person";
import Settings from "@mui/icons-material/Settings";
import Apps from "@mui/icons-material/Apps";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import Translate from "@mui/icons-material/Translate";
import Language from "@mui/icons-material/Language";
import Palette from "@mui/icons-material/Palette";
import DateRange from "@mui/icons-material/DateRange";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import {
    useFoodpilotContext,
    BRAND,
    MODIFY,
    DELETE,
    CREATE,
    PRODUCT_LINE,
    ORGANIZATION,
    COMPANY_SECTOR,
    USER,
    INGREDIENT_LINE,
    PRODUCT_SETTINGS_ORGANIZATION,
    TAG_TYPE,
    VIEW,
    MEDIA_CONTENT,
    CAMPAIGN,
    PUCK_PAGE,
    ROLE,
    PUCK_EXTENSION,
    NAVIGATION_MENU,
    AUDIT_LOG,
    SCORE_TYPE,
    ACTION_PLAN_TEMPLATE,
    LOCALE,
    CUSTOM_THEME,
    COHORT_DEFINITION,
} from "src/context/FoodpilotContext";
import { hasPermissionOnPlatformOrAnyOrganization } from "src/context/permissionMatching";
import { URLHandler } from "src/services/URLHandler";
import { canAdministerSuppliers } from "src/pages/Settings/Supplier/supplierAdministration";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { APP_INTEGRATION_FEATURE_SWITCH } from "src/api/client-api/applications/constants";

export type SettingsLink = {
    title: string;
    to: string;
    icon: ReactNode;
    visible: boolean;
};

export const useSettingsLinks = (): SettingsLink[] => {
    const { t } = useTranslation();
    const { canUser, isAdmin, user } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const canViewCmsPages = canUser(CREATE, PUCK_PAGE) || canUser(MODIFY, PUCK_PAGE) || canUser(DELETE, PUCK_PAGE);
    const canViewDynaBlocks = canUser(VIEW, PUCK_EXTENSION);

    return [
        {
            title: "Media Library",
            to: URLHandler.settings.mediaLibrary.root(),
            icon: <FoodsIcon size={2} icon="notes" />,
            visible: canUser(VIEW, MEDIA_CONTENT),
        },
        {
            title: "Gestion des ICVs",
            to: URLHandler.settings.icvs.list(),
            icon: <FoodsIcon size={2} icon="settings" />,
            visible: isAdmin() && isFeatureActivated("FS-icv-management"),
        },
        {
            title: t("ingredientReferences.title", "Référentiel ingrédients"),
            to: URLHandler.settings.ingredientReferences.list(),
            icon: <FoodsIcon size={2} icon="settings" />,
            visible:
                isAdmin() &&
                isFeatureActivated("FS-entity-ingredients") &&
                isFeatureActivated("FS-platform-ingredient-references"),
        },
        {
            title: "CMS",
            to:
                canViewDynaBlocks && !canViewCmsPages ?
                    URLHandler.settings.configurationPath("customization-languages", "app-blocks")
                :   URLHandler.settings.cms.root(),
            icon: <FoodsIcon size={2} icon="settings" />,
            visible: canViewCmsPages || canViewDynaBlocks,
        },
        {
            title: t("settings.consent.title", "Consent governance"),
            to: URLHandler.settings.consentGovernance(),
            icon: <FoodsIcon size={2} icon="settings" />,
            visible: isAdmin() && isFeatureActivated(CONSENT_GOVERNANCE_FEATURE_SWITCH),
        },
        {
            title: t("tableau_de_bord"),
            to: URLHandler.settings.list(),
            icon: <Dashboard />,
            visible: isAdmin(),
        },
        {
            title: "My organization",
            to: URLHandler.settings.myOrganization(),
            icon: <Apartment />,
            visible: isAdmin(),
        },
        {
            title: t("settings.platform.platform_setup.scores.title", "Scores"),
            to: URLHandler.settings.configurationPath("platform-setup", "scores"),
            icon: <Settings />,
            visible: canUser(VIEW, SCORE_TYPE),
        },
        {
            title: t("settings.campaign"),
            to: URLHandler.settings.configurationPath("platform-setup", "campaigns"),
            icon: <DateRange />,
            visible: canUser(VIEW, CAMPAIGN),
        },
        {
            title: t("settings.platform.platform_setup.navigation.title", "Navigation"),
            to: URLHandler.settings.configurationPath("platform-setup", "navigation"),
            icon: <Settings />,
            visible: canUser(VIEW, NAVIGATION_MENU),
        },
        {
            title: t("settings.action_plan_templates.title", "Action plan templates"),
            to: URLHandler.settings.configurationPath("platform-setup", "action-plan-templates"),
            icon: <FoodsIcon size={2} icon="checkList" />,
            visible: canUser(VIEW, ACTION_PLAN_TEMPLATE) && isFeatureActivated("FS-actionPlan"),
        },
        {
            title: t("settings.platform.access_permissions.audit_log.title", "Audit log"),
            to: URLHandler.settings.auditLog(),
            icon: <Settings />,
            visible: hasPermissionOnPlatformOrAnyOrganization(user.trustedUser, VIEW, AUDIT_LOG),
        },
        {
            title: t("settings.lines"),
            to: URLHandler.settings.productLine.list(),
            icon: <Class />,
            visible: canUser(CREATE, PRODUCT_LINE) || canUser(MODIFY, PRODUCT_LINE) || canUser(DELETE, PRODUCT_LINE),
        },
        {
            title: t("settings.company"),
            to: URLHandler.settings.companies.list(),
            icon: <Apartment />,
            visible: canUser(VIEW, ORGANIZATION),
        },
        {
            title: t("settings.companysectors"),
            to: URLHandler.settings.companySectors(),
            icon: <EmojiTransportation />,
            visible:
                canUser(CREATE, COMPANY_SECTOR) || canUser(MODIFY, COMPANY_SECTOR) || canUser(DELETE, COMPANY_SECTOR),
        },
        {
            title: "Types d'organisation",
            to: URLHandler.settings.organizationTypes(),
            icon: <Apartment />,
            visible: isAdmin(),
        },
        {
            title: t("settings.providers"),
            to: URLHandler.settings.providers.list(),
            icon: <LocalShipping />,
            visible: canAdministerSuppliers(user.trustedUser, VIEW),
        },
        {
            title: t("settings.users"),
            to: URLHandler.settings.users.list(),
            icon: <Person />,
            visible: canUser(VIEW, USER) || canUser(CREATE, USER) || canUser(MODIFY, USER) || canUser(DELETE, USER),
        },
        {
            title: t("roles"),
            to: URLHandler.settings.roles.list(),
            icon: <Person />,
            visible: canUser(VIEW, ROLE),
        },
        {
            title: t("settings.platform.access_permissions.permissions.title", "Permissions"),
            to: URLHandler.settings.configurationPath("access-permissions", "permissions"),
            icon: <Settings />,
            visible: canUser(CREATE, ROLE) || canUser(MODIFY, ROLE),
        },

        {
            title: "CsrTheme",
            to: URLHandler.settings.csrTheme.list(),
            icon: <Person />,
            visible: isAdmin(),
        },
        {
            title: t("settings.customThemes", "Custom themes"),
            to: URLHandler.settings.customTheme.list(),
            icon: <Palette />,
            visible: canUser(VIEW, CUSTOM_THEME),
        },
        {
            title: "Brand",
            to: URLHandler.settings.brands.list(),
            icon: <Settings />,
            visible: canUser(CREATE, BRAND) || canUser(MODIFY, BRAND) || canUser(DELETE, BRAND),
        },
        {
            title: "Gestion des Tags",
            to: URLHandler.settings.tags.overview(),
            icon: <Settings />,
            visible: canUser(CREATE, TAG_TYPE) || canUser(MODIFY, TAG_TYPE) || canUser(DELETE, TAG_TYPE),
        },
        {
            title: "Filière",
            to: URLHandler.settings.ingredientLine.list(),
            icon: <Settings />,
            visible:
                canUser(CREATE, INGREDIENT_LINE) ||
                canUser(MODIFY, INGREDIENT_LINE) ||
                canUser(DELETE, INGREDIENT_LINE),
        },
        {
            title: "Applications",
            to: URLHandler.settings.applications.list(),
            icon: <Apps />,
            visible: isAdmin() && isFeatureActivated(APP_INTEGRATION_FEATURE_SWITCH),
        },
        {
            title: t("settings.references.custom_data_lists.title", "Custom data lists"),
            to: URLHandler.settings.dataListsValuesOverrides(),
            icon: <FormatListBulleted />,
            visible: isAdmin(),
        },
        {
            title: t("settings.references.sector_score_targets.title", "Sector score targets"),
            to: URLHandler.settings.entityTargetScoreAssignments(),
            icon: <FoodsIcon size={2} icon="scoresIndicators" />,
            visible: isAdmin(),
        },
        {
            title: "Additional object parameters",
            to: URLHandler.settings.jsonAttributes(),
            icon: <Apps />,
            visible: isAdmin(),
        },
        {
            title: t("settings.cohorts.title", "Cohort definitions"),
            to: URLHandler.settings.cohortDefinitions(),
            icon: <FoodsIcon size={2} icon="scoresIndicators" />,
            visible: canUser(VIEW, COHORT_DEFINITION),
        },
        {
            title: t("settings.packagings.ssq", "Packaging's SSQ"),
            to: URLHandler.settings.packagings.ssq(),
            icon: <FoodsIcon size={2} icon="packaging" />,
            visible: canUser(MODIFY, PRODUCT_SETTINGS_ORGANIZATION),
        },

        {
            title: t("settings.applicationparameter"),
            to: URLHandler.settings.appParams(),
            icon: <Settings />,
            visible: isAdmin(),
        },
        {
            title: t("translations.management.title", "Traductions"),
            to: URLHandler.settings.translations.management(),
            icon: <Translate />,
            visible: isAdmin(),
        },
        {
            title: t("languages.management.title", "Languages"),
            to: URLHandler.settings.configurationPath("customization-languages", "available-languages"),
            icon: <Language />,
            visible: canUser(MODIFY, LOCALE),
        },
        {
            title: "Theme",
            to: URLHandler.settings.theme(),
            icon: <Palette />,
            visible: isAdmin(),
        },
        {
            title: t("settings.questionnaires", "Questionnaires"),
            to: URLHandler.settings.questionnaires.list(),
            icon: <FoodsIcon size={2} icon="notes" />,
            visible: isAdmin(),
        },
    ];
};
